// React-Query hooks for the Wharf thread + message APIs.

import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

/**
 * Optimistically drop a thread row from every cached threads-list page so
 * the UI updates the instant the user taps Archive / Delete / Snooze. On
 * error we restore the snapshot; either way we eventually invalidate so the
 * server is the source of truth.
 */
async function snapshotAndRemoveThread(qc: QueryClient, threadId: number) {
  await qc.cancelQueries({ queryKey: ['threads'] });
  const snapshots = qc.getQueriesData<unknown>({ queryKey: ['threads'] });
  qc.setQueriesData<{ pages?: { threads: { id: number }[] }[] }>(
    { queryKey: ['threads'] },
    (old) => {
      if (!old?.pages) return old;
      return {
        ...old,
        pages: old.pages.map((p) => ({
          ...p,
          threads: p.threads.filter((t) => t.id !== threadId),
        })),
      } as typeof old;
    },
  );
  return snapshots;
}

function restoreSnapshots(qc: QueryClient, snapshots: ReturnType<QueryClient['getQueriesData']>) {
  for (const [key, data] of snapshots) qc.setQueryData(key, data);
}

export interface ThreadTagRef {
  id: number;
  name: string;
  color: string;
}

export interface ThreadListItem {
  id: number;
  accountId: number;
  gmailThreadId: string;
  subject: string;
  snippet: string;
  lastMessageAt: string;
  messageCount: number;
  unread: boolean;
  tags: ThreadTagRef[];
  trashedAt: string | null;
  trashedBy: TrashedBy | null;
  snoozedUntil: string | null;
  snoozedByUserId: number | null;
  /** From: header of the latest message in this thread — drives the sender
   *  avatar + display name on the row. May be a bare address or a
   *  display-name + address string ("Marianne <m@…>"). */
  latestFrom: string | null;
  /** Conversation status — 'done' = handled (lives in the Done folder). */
  status: 'open' | 'done';
  /** Whether the thread carries Gmail's STARRED label. */
  starred: boolean;
  /** Who this conversation is assigned to, if anyone. */
  assignee: { id: number; name: string; email: string; picture: string | null } | null;
  /** All-accounts view only: other inboxes that received the same email
   *  (the shown row's account is excluded). Empty for per-account views. */
  alsoInAccounts: { id: number; email: string }[];
}

export interface ThreadListPage {
  threads: ThreadListItem[];
  nextCursor: string | null;
  searchTruncated?: boolean;
}

export interface MessageAttachment {
  index: number;
  filename: string;
  mimeType: string;
  sizeBytes: number;
}

export interface ThreadMessage {
  id: number;
  gmailMessageId: string;
  from: string | null;
  to: string[];
  cc: string[];
  bcc: string[];
  sentAt: string;
  bodyHtml: string | null;
  bodyText: string | null;
  attachments: MessageAttachment[];
}

export interface ThreadDetail {
  thread: ThreadListItem & { tags: ThreadTagRef[] };
  messages: ThreadMessage[];
}

const PAGE_LIMIT = 50;

export type Folder =
  | 'all'
  | 'inbox'
  | 'assigned'
  | 'done'
  | 'sent'
  | 'starred'
  | 'drafts'
  | 'snoozed'
  | 'spam'
  | 'trash';

export interface TrashedBy {
  id: number;
  name: string;
  email: string;
}

export function useThreads(
  accountId: number | null,
  folder: Folder = 'all',
  q: string = '',
) {
  const query = q.trim();
  return useInfiniteQuery<ThreadListPage>({
    queryKey: ['threads', accountId ?? 'all', folder, query],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_LIMIT));
      params.set('folder', folder);
      if (accountId) params.set('account_id', String(accountId));
      if (pageParam) params.set('before', pageParam as string);
      if (query) params.set('q', query);
      const res = await fetch(`/api/threads?${params}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    getNextPageParam: (last) => last.nextCursor,
    staleTime: query ? 0 : 10_000,
    // Keep the list lively while the user is on the page so new mail + read-
    // state changes from teammates appear without manual refresh. Disable
    // polling during an active search to avoid surprise re-runs.
    refetchInterval: query ? false : 60_000,
    refetchOnWindowFocus: !query,
  });
}

export function useThread(threadId: number | null) {
  const qc = useQueryClient();
  return useQuery<ThreadDetail>({
    queryKey: ['thread', threadId],
    enabled: threadId != null,
    queryFn: async () => {
      const res = await fetch(`/api/threads/${threadId}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as ThreadDetail;
      // GET /:id auto-marks read server-side — invalidate the list so the
      // bold/dot disappears on the open row.
      void qc.invalidateQueries({ queryKey: ['threads'] });
      return json;
    },
  });
}

export function useUnarchiveThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/threads/${id}/unarchive`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || body.error || `HTTP ${res.status}`);
      }
      return res.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['threads'] });
    },
  });
}

export function useTrashThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const r = await fetch(`/api/threads/${id}/trash`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || j.error || `HTTP ${r.status}`);
      }
      return r.json();
    },
    onMutate: async (id) => ({ snapshots: await snapshotAndRemoveThread(qc, id) }),
    onError: (err, _id, ctx) => {
      if (ctx?.snapshots) restoreSnapshots(qc, ctx.snapshots);
      toast.error(err instanceof Error ? err.message : 'Failed to delete');
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['threads'] });
      void qc.invalidateQueries({ queryKey: ['thread'] });
    },
  });
}

export function useUntrashThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const r = await fetch(`/api/threads/${id}/untrash`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || j.error || `HTTP ${r.status}`);
      }
      return r.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['threads'] });
      void qc.invalidateQueries({ queryKey: ['thread'] });
    },
  });
}

export function useSpamThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const r = await fetch(`/api/threads/${id}/spam`, { method: 'POST', credentials: 'include' });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || j.error || `HTTP ${r.status}`);
      }
      return r.json();
    },
    onMutate: async (id) => ({ snapshots: await snapshotAndRemoveThread(qc, id) }),
    onError: (err, _id, ctx) => {
      if (ctx?.snapshots) restoreSnapshots(qc, ctx.snapshots);
      toast.error(err instanceof Error ? err.message : 'Failed to report spam');
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['threads'] });
      void qc.invalidateQueries({ queryKey: ['thread'] });
    },
  });
}

export function useUnspamThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const r = await fetch(`/api/threads/${id}/unspam`, { method: 'POST', credentials: 'include' });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || j.error || `HTTP ${r.status}`);
      }
      return r.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['threads'] });
      void qc.invalidateQueries({ queryKey: ['thread'] });
    },
  });
}

export interface BlockDomainResult {
  ok: boolean;
  domain: string;
  filterId?: string;
  error?: string;
}

export function useBlockDomain() {
  const qc = useQueryClient();
  return useMutation<BlockDomainResult, Error, { id: number; domain?: string }>({
    mutationFn: async ({ id, domain }) => {
      const r = await fetch(`/api/threads/${id}/block-domain`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ domain }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        // 409 needs_reauth is surfaced specially by the caller.
        const e = new Error(j.error || `HTTP ${r.status}`);
        (e as Error & { code?: string }).code = j.error;
        throw e;
      }
      return j;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['threads'] });
      void qc.invalidateQueries({ queryKey: ['thread'] });
    },
  });
}

/** Mark a conversation done — handled. Optimistically drops it from the
 *  current list (it leaves Inbox/All for the Done folder). */
export function useMarkDone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const r = await fetch(`/api/threads/${id}/done`, { method: 'POST', credentials: 'include' });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || j.error || `HTTP ${r.status}`);
      }
      return r.json();
    },
    onMutate: async (id) => ({ snapshots: await snapshotAndRemoveThread(qc, id) }),
    onError: (err, _id, ctx) => {
      if (ctx?.snapshots) restoreSnapshots(qc, ctx.snapshots);
      toast.error(err instanceof Error ? err.message : 'Failed to mark done');
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['threads'] });
      void qc.invalidateQueries({ queryKey: ['thread'] });
    },
  });
}

/** Reopen a done conversation. */
export function useReopenThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const r = await fetch(`/api/threads/${id}/reopen`, { method: 'POST', credentials: 'include' });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || j.error || `HTTP ${r.status}`);
      }
      return r.json();
    },
    onMutate: async (id) => ({ snapshots: await snapshotAndRemoveThread(qc, id) }),
    onError: (err, _id, ctx) => {
      if (ctx?.snapshots) restoreSnapshots(qc, ctx.snapshots);
      toast.error(err instanceof Error ? err.message : 'Failed to reopen');
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['threads'] });
      void qc.invalidateQueries({ queryKey: ['thread'] });
    },
  });
}

export function useArchiveThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/threads/${id}/archive`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || body.error || `HTTP ${res.status}`);
      }
      return res.json();
    },
    onMutate: async (id) => ({ snapshots: await snapshotAndRemoveThread(qc, id) }),
    onError: (err, _id, ctx) => {
      if (ctx?.snapshots) restoreSnapshots(qc, ctx.snapshots);
      toast.error(err instanceof Error ? err.message : 'Failed to archive');
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['threads'] });
    },
  });
}

/** Toggle Gmail's STARRED label. Optimistically flips `starred` in the list +
 *  detail caches (the thread stays in place — unlike archive/snooze). */
export function useToggleStar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, starred }: { id: number; starred: boolean }) => {
      const r = await fetch(`/api/threads/${id}/${starred ? 'star' : 'unstar'}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || j.error || `HTTP ${r.status}`);
      }
      return r.json() as Promise<{ ok: true; starred: boolean }>;
    },
    onMutate: async ({ id, starred }) => {
      await qc.cancelQueries({ queryKey: ['threads'] });
      const listSnap = qc.getQueriesData<{ pages?: ThreadListPage[] }>({ queryKey: ['threads'] });
      qc.setQueriesData<{ pages?: ThreadListPage[] }>({ queryKey: ['threads'] }, (old) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((p) => ({
            ...p,
            threads: p.threads.map((t) => (t.id === id ? { ...t, starred } : t)),
          })),
        };
      });
      const detailKey = ['thread', id] as const;
      const detailSnap = qc.getQueryData<ThreadDetail>(detailKey);
      if (detailSnap) {
        qc.setQueryData<ThreadDetail>(detailKey, {
          ...detailSnap,
          thread: { ...detailSnap.thread, starred },
        });
      }
      return { listSnap, detailSnap, id };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.listSnap) for (const [key, data] of ctx.listSnap) qc.setQueryData(key, data);
      if (ctx?.detailSnap) qc.setQueryData(['thread', ctx.id], ctx.detailSnap);
      toast.error(err instanceof Error ? err.message : 'Failed to update star');
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['threads'] });
    },
  });
}

export function useSnoozeThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, until }: { id: number; until: string }) => {
      const r = await fetch(`/api/threads/${id}/snooze`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ until }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || j.error || `HTTP ${r.status}`);
      }
      return r.json();
    },
    onMutate: async ({ id }) => ({ snapshots: await snapshotAndRemoveThread(qc, id) }),
    onError: (err, _vars, ctx) => {
      if (ctx?.snapshots) restoreSnapshots(qc, ctx.snapshots);
      toast.error(err instanceof Error ? err.message : 'Failed to snooze');
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['threads'] });
      void qc.invalidateQueries({ queryKey: ['thread'] });
    },
  });
}

export function useUnsnoozeThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const r = await fetch(`/api/threads/${id}/unsnooze`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || j.error || `HTTP ${r.status}`);
      }
      return r.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['threads'] });
      void qc.invalidateQueries({ queryKey: ['thread'] });
    },
  });
}

export function useMarkUnread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/threads/${id}/unread`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['threads'] });
    },
  });
}
